import { useState } from 'react';
import {
  CustomPage, PageSection, PageColumn, PageBlock, PageBlockType,
  SectionLayout, SECTION_LAYOUTS, ColumnWidth,
  PageBlockHeading, PageBlockText, PageBlockImage, PageBlockButton,
  PageBlockDivider, PageBlockSpacer, PageBlockHero,
  PageBlockFaqList, PageBlockFeaturesGrid, PageBlockStats, PageBlockTeam,
  FaqItem, FeatureGridItem, StatItem, TeamMember,
} from '@/lib/themeConfig';
import ImageUploader from '@/pages/tema/components/ImageUploader';

interface Props {
  page: CustomPage;
  onSave: (page: CustomPage) => void;
  onBack: () => void;
}

type EditorTab = 'layout' | 'seo';

// ─── ID Generator ─────────────────────────────────────────────────────────────
function uid() { return Math.random().toString(36).slice(2, 10); }

// ─── Block Factory ────────────────────────────────────────────────────────────
function createBlock(type: PageBlockType): PageBlock {
  const id = uid();
  switch (type) {
    case 'heading': return { type, id, text: 'Başlık', level: 'h2', align: 'left', color: '#1c1917' };
    case 'text': return { type, id, content: 'Metin içeriği buraya gelecek.', align: 'left', color: '#57534e' };
    case 'image': return { type, id, image_url: 'https://public.readdy.ai/ai/img_res/9471ff8dca715568269e285818174b97.jpg', alt: 'Görsel', caption: '', border_radius: 8 };
    case 'button': return { type, id, label: 'Butona Tıkla', href: '/', variant: 'primary', align: 'left', size: 'md' };
    case 'divider': return { type, id, color: '#e7e5e4' };
    case 'spacer': return { type, id, height: 40 };
    case 'hero': return { type, id, title: 'Hero Başlık', subtitle: 'Alt Başlık', description: 'Açıklama metni.', cta_text: 'Devam Et', cta_link: '/', image_url: 'https://readdy.ai/api/search-image?query=modern%20minimal%20hero%20background%20abstract%20gradient%20warm%20tones%20professional%20website%20elegant&width=1400&height=600&seq=ph1&orientation=landscape', overlay_opacity: 40 };
    case 'faq_list': return { type, id, title: 'Sık Sorulan Sorular', items: [{ id: uid(), question: 'Soru 1?', answer: 'Cevap 1.' }] };
    case 'features_grid': return { type, id, title: 'Özellikler', items: [{ id: uid(), icon: 'ri-star-line', title: 'Özellik', description: 'Açıklama.' }] };
    case 'stats': return { type, id, title: 'Rakamlarla', items: [{ id: uid(), value: '100+', label: 'Müşteri' }] };
    case 'team': return { type, id, title: 'Ekibimiz', members: [{ id: uid(), name: 'Ad Soyad', role: 'Pozisyon', bio: 'Biyografi.', image_url: 'https://readdy.ai/api/search-image?query=professional%20portrait%20person%20smiling%20minimal%20background%20warm%20tones%20business%20headshot&width=400&height=400&seq=pt1&orientation=squarish' }] };
  }
}

// ─── Section Factory ──────────────────────────────────────────────────────────
function createSection(layout: SectionLayout): PageSection {
  const def = SECTION_LAYOUTS.find(l => l.id === layout)!;
  return {
    id: uid(),
    layout,
    background_color: '#ffffff',
    padding_y: 'md',
    columns: def.columns.map(w => ({ id: uid(), width: w, blocks: [] })),
  };
}

// ─── Block Type Definitions ───────────────────────────────────────────────────
const BLOCK_TYPES: { type: PageBlockType; label: string; icon: string; group: string }[] = [
  { type: 'heading', label: 'Başlık', icon: 'ri-heading', group: 'Temel' },
  { type: 'text', label: 'Metin', icon: 'ri-text', group: 'Temel' },
  { type: 'image', label: 'Görsel', icon: 'ri-image-2-line', group: 'Temel' },
  { type: 'button', label: 'Buton', icon: 'ri-cursor-line', group: 'Temel' },
  { type: 'divider', label: 'Ayraç', icon: 'ri-separator', group: 'Temel' },
  { type: 'spacer', label: 'Boşluk', icon: 'ri-space', group: 'Temel' },
  { type: 'hero', label: 'Hero Banner', icon: 'ri-image-line', group: 'Gelişmiş' },
  { type: 'faq_list', label: 'SSS Listesi', icon: 'ri-question-answer-line', group: 'Gelişmiş' },
  { type: 'features_grid', label: 'Özellikler Grid', icon: 'ri-grid-line', group: 'Gelişmiş' },
  { type: 'stats', label: 'İstatistikler', icon: 'ri-bar-chart-line', group: 'Gelişmiş' },
  { type: 'team', label: 'Ekip', icon: 'ri-team-line', group: 'Gelişmiş' },
];

// ─── Individual Block Editors ─────────────────────────────────────────────────

function HeadingEditor({ block, onChange }: { block: PageBlockHeading; onChange: (b: PageBlockHeading) => void }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-stone-600 mb-1">Metin</label>
        <input type="text" value={block.text} onChange={e => onChange({ ...block, text: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Seviye</label>
          <select value={block.level} onChange={e => onChange({ ...block, level: e.target.value as PageBlockHeading['level'] })} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white">
            <option value="h1">H1 — Büyük</option>
            <option value="h2">H2 — Orta</option>
            <option value="h3">H3 — Küçük</option>
            <option value="h4">H4 — Çok Küçük</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Hizalama</label>
          <div className="flex gap-1">
            {(['left', 'center', 'right'] as const).map(a => (
              <button key={a} onClick={() => onChange({ ...block, align: a })} className={`flex-1 py-2 rounded-lg text-xs border transition-all cursor-pointer whitespace-nowrap ${block.align === a ? 'bg-amber-600 text-white border-amber-600' : 'border-stone-200 text-stone-600 hover:border-stone-300'}`}>
                {a === 'left' ? 'Sol' : a === 'center' ? 'Orta' : 'Sağ'}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-stone-600 mb-1">Renk</label>
        <div className="flex items-center gap-2">
          <input type="color" value={block.color} onChange={e => onChange({ ...block, color: e.target.value })} className="w-9 h-9 rounded-lg border border-stone-200 cursor-pointer" />
          <input type="text" value={block.color} onChange={e => onChange({ ...block, color: e.target.value })} className="flex-1 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
        </div>
      </div>
    </div>
  );
}

function TextEditor({ block, onChange }: { block: PageBlockText; onChange: (b: PageBlockText) => void }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-stone-600 mb-1">İçerik</label>
        <textarea value={block.content} onChange={e => onChange({ ...block, content: e.target.value })} rows={5} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Hizalama</label>
          <div className="flex gap-1">
            {(['left', 'center', 'right'] as const).map(a => (
              <button key={a} onClick={() => onChange({ ...block, align: a })} className={`flex-1 py-2 rounded-lg text-xs border transition-all cursor-pointer whitespace-nowrap ${block.align === a ? 'bg-amber-600 text-white border-amber-600' : 'border-stone-200 text-stone-600 hover:border-stone-300'}`}>
                {a === 'left' ? 'Sol' : a === 'center' ? 'Orta' : 'Sağ'}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Renk</label>
          <div className="flex items-center gap-2">
            <input type="color" value={block.color} onChange={e => onChange({ ...block, color: e.target.value })} className="w-9 h-9 rounded-lg border border-stone-200 cursor-pointer" />
            <input type="text" value={block.color} onChange={e => onChange({ ...block, color: e.target.value })} className="flex-1 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
        </div>
      </div>
    </div>
  );
}

function ImageEditor({ block, onChange }: { block: PageBlockImage; onChange: (b: PageBlockImage) => void }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-stone-600 mb-1">Görsel</label>
        <ImageUploader value={block.image_url} onChange={url => onChange({ ...block, image_url: url })} />
      </div>
      <div>
        <label className="block text-xs font-medium text-stone-600 mb-1">Alt Metin (SEO)</label>
        <input type="text" value={block.alt} onChange={e => onChange({ ...block, alt: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
      </div>
      <div>
        <label className="block text-xs font-medium text-stone-600 mb-1">Altyazı</label>
        <input type="text" value={block.caption} onChange={e => onChange({ ...block, caption: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
      </div>
      <div>
        <label className="block text-xs font-medium text-stone-600 mb-1">Köşe Yuvarlaklığı: {block.border_radius}px</label>
        <input type="range" min={0} max={24} value={block.border_radius} onChange={e => onChange({ ...block, border_radius: Number(e.target.value) })} className="w-full accent-amber-600" />
      </div>
    </div>
  );
}

function ButtonEditor({ block, onChange }: { block: PageBlockButton; onChange: (b: PageBlockButton) => void }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-stone-600 mb-1">Buton Metni</label>
        <input type="text" value={block.label} onChange={e => onChange({ ...block, label: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
      </div>
      <div>
        <label className="block text-xs font-medium text-stone-600 mb-1">Link</label>
        <input type="text" value={block.href} onChange={e => onChange({ ...block, href: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Stil</label>
          <select value={block.variant} onChange={e => onChange({ ...block, variant: e.target.value as PageBlockButton['variant'] })} className="w-full border border-stone-200 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white">
            <option value="primary">Dolu</option>
            <option value="secondary">İkincil</option>
            <option value="outline">Çerçeveli</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Boyut</label>
          <select value={block.size} onChange={e => onChange({ ...block, size: e.target.value as PageBlockButton['size'] })} className="w-full border border-stone-200 rounded-lg px-2 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white">
            <option value="sm">Küçük</option>
            <option value="md">Orta</option>
            <option value="lg">Büyük</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Hizalama</label>
          <div className="flex gap-1">
            {(['left', 'center', 'right'] as const).map(a => (
              <button key={a} onClick={() => onChange({ ...block, align: a })} className={`flex-1 py-2 rounded-lg text-xs border transition-all cursor-pointer whitespace-nowrap ${block.align === a ? 'bg-amber-600 text-white border-amber-600' : 'border-stone-200 text-stone-600 hover:border-stone-300'}`}>
                {a === 'left' ? 'Sol' : a === 'center' ? 'Orta' : 'Sağ'}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function DividerEditor({ block, onChange }: { block: PageBlockDivider; onChange: (b: PageBlockDivider) => void }) {
  return (
    <div>
      <label className="block text-xs font-medium text-stone-600 mb-1">Renk</label>
      <div className="flex items-center gap-2">
        <input type="color" value={block.color} onChange={e => onChange({ ...block, color: e.target.value })} className="w-9 h-9 rounded-lg border border-stone-200 cursor-pointer" />
        <input type="text" value={block.color} onChange={e => onChange({ ...block, color: e.target.value })} className="flex-1 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
      </div>
    </div>
  );
}

function SpacerEditor({ block, onChange }: { block: PageBlockSpacer; onChange: (b: PageBlockSpacer) => void }) {
  return (
    <div>
      <label className="block text-xs font-medium text-stone-600 mb-1">Yükseklik: {block.height}px</label>
      <input type="range" min={8} max={200} step={4} value={block.height} onChange={e => onChange({ ...block, height: Number(e.target.value) })} className="w-full accent-amber-600" />
    </div>
  );
}

function HeroEditor({ block, onChange }: { block: PageBlockHero; onChange: (b: PageBlockHero) => void }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-stone-600 mb-1">Başlık</label>
        <input type="text" value={block.title} onChange={e => onChange({ ...block, title: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
      </div>
      <div>
        <label className="block text-xs font-medium text-stone-600 mb-1">Alt Başlık</label>
        <input type="text" value={block.subtitle} onChange={e => onChange({ ...block, subtitle: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
      </div>
      <div>
        <label className="block text-xs font-medium text-stone-600 mb-1">Açıklama</label>
        <textarea value={block.description} onChange={e => onChange({ ...block, description: e.target.value })} rows={3} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Buton Metni</label>
          <input type="text" value={block.cta_text} onChange={e => onChange({ ...block, cta_text: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-1">Buton Linki</label>
          <input type="text" value={block.cta_link} onChange={e => onChange({ ...block, cta_link: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-stone-600 mb-1">Karartma: %{block.overlay_opacity}</label>
        <input type="range" min={0} max={80} value={block.overlay_opacity} onChange={e => onChange({ ...block, overlay_opacity: Number(e.target.value) })} className="w-full accent-amber-600" />
      </div>
      <div>
        <label className="block text-xs font-medium text-stone-600 mb-1">Arka Plan Görseli</label>
        <ImageUploader value={block.image_url} onChange={url => onChange({ ...block, image_url: url })} />
      </div>
    </div>
  );
}

function FaqEditor({ block, onChange }: { block: PageBlockFaqList; onChange: (b: PageBlockFaqList) => void }) {
  const add = () => onChange({ ...block, items: [...block.items, { id: uid(), question: 'Yeni Soru?', answer: 'Cevap.' }] });
  const remove = (id: string) => onChange({ ...block, items: block.items.filter(i => i.id !== id) });
  const update = (id: string, p: Partial<FaqItem>) => onChange({ ...block, items: block.items.map(i => i.id === id ? { ...i, ...p } : i) });
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-stone-600 mb-1">Başlık</label>
        <input type="text" value={block.title} onChange={e => onChange({ ...block, title: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
      </div>
      {block.items.map((item, idx) => (
        <div key={item.id} className="bg-stone-50 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-stone-500">Soru {idx + 1}</span>
            <button onClick={() => remove(item.id)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-50 text-stone-400 hover:text-red-500 cursor-pointer"><i className="ri-close-line text-xs" /></button>
          </div>
          <input type="text" value={item.question} onChange={e => update(item.id, { question: e.target.value })} placeholder="Soru" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white" />
          <textarea value={item.answer} onChange={e => update(item.id, { answer: e.target.value })} rows={2} placeholder="Cevap" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none bg-white" />
        </div>
      ))}
      <button onClick={add} className="w-full py-2 border border-dashed border-stone-300 rounded-lg text-xs text-stone-500 hover:border-amber-400 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap">+ Soru Ekle</button>
    </div>
  );
}

function FeaturesEditor({ block, onChange }: { block: PageBlockFeaturesGrid; onChange: (b: PageBlockFeaturesGrid) => void }) {
  const add = () => onChange({ ...block, items: [...block.items, { id: uid(), icon: 'ri-star-line', title: 'Özellik', description: 'Açıklama.' }] });
  const remove = (id: string) => onChange({ ...block, items: block.items.filter(i => i.id !== id) });
  const update = (id: string, p: Partial<FeatureGridItem>) => onChange({ ...block, items: block.items.map(i => i.id === id ? { ...i, ...p } : i) });
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-stone-600 mb-1">Başlık</label>
        <input type="text" value={block.title} onChange={e => onChange({ ...block, title: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
      </div>
      {block.items.map((item, idx) => (
        <div key={item.id} className="bg-stone-50 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-stone-500">Özellik {idx + 1}</span>
            <button onClick={() => remove(item.id)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-50 text-stone-400 hover:text-red-500 cursor-pointer"><i className="ri-close-line text-xs" /></button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input type="text" value={item.icon} onChange={e => update(item.id, { icon: e.target.value })} placeholder="ri-star-line" className="border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white" />
            <input type="text" value={item.title} onChange={e => update(item.id, { title: e.target.value })} placeholder="Başlık" className="border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white" />
          </div>
          <input type="text" value={item.description} onChange={e => update(item.id, { description: e.target.value })} placeholder="Açıklama" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white" />
        </div>
      ))}
      <button onClick={add} className="w-full py-2 border border-dashed border-stone-300 rounded-lg text-xs text-stone-500 hover:border-amber-400 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap">+ Özellik Ekle</button>
    </div>
  );
}

function StatsEditor({ block, onChange }: { block: PageBlockStats; onChange: (b: PageBlockStats) => void }) {
  const add = () => onChange({ ...block, items: [...block.items, { id: uid(), value: '0', label: 'Etiket' }] });
  const remove = (id: string) => onChange({ ...block, items: block.items.filter(i => i.id !== id) });
  const update = (id: string, p: Partial<StatItem>) => onChange({ ...block, items: block.items.map(i => i.id === id ? { ...i, ...p } : i) });
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-stone-600 mb-1">Başlık</label>
        <input type="text" value={block.title} onChange={e => onChange({ ...block, title: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
      </div>
      {block.items.map((item, idx) => (
        <div key={item.id} className="bg-stone-50 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-stone-500">İstatistik {idx + 1}</span>
            <button onClick={() => remove(item.id)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-50 text-stone-400 hover:text-red-500 cursor-pointer"><i className="ri-close-line text-xs" /></button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input type="text" value={item.value} onChange={e => update(item.id, { value: e.target.value })} placeholder="100+" className="border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white" />
            <input type="text" value={item.label} onChange={e => update(item.id, { label: e.target.value })} placeholder="Müşteri" className="border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white" />
          </div>
        </div>
      ))}
      <button onClick={add} className="w-full py-2 border border-dashed border-stone-300 rounded-lg text-xs text-stone-500 hover:border-amber-400 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap">+ İstatistik Ekle</button>
    </div>
  );
}

function TeamEditor({ block, onChange }: { block: PageBlockTeam; onChange: (b: PageBlockTeam) => void }) {
  const add = () => onChange({ ...block, members: [...block.members, { id: uid(), name: 'Ad Soyad', role: 'Pozisyon', bio: 'Biyografi.', image_url: 'https://readdy.ai/api/search-image?query=professional%20portrait%20person%20smiling%20minimal%20background%20warm%20tones%20business%20headshot&width=400&height=400&seq=pt2&orientation=squarish' }] });
  const remove = (id: string) => onChange({ ...block, members: block.members.filter(m => m.id !== id) });
  const update = (id: string, p: Partial<TeamMember>) => onChange({ ...block, members: block.members.map(m => m.id === id ? { ...m, ...p } : m) });
  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-stone-600 mb-1">Başlık</label>
        <input type="text" value={block.title} onChange={e => onChange({ ...block, title: e.target.value })} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
      </div>
      {block.members.map((m, idx) => (
        <div key={m.id} className="bg-stone-50 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-stone-500">Üye {idx + 1}</span>
            <button onClick={() => remove(m.id)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-50 text-stone-400 hover:text-red-500 cursor-pointer"><i className="ri-close-line text-xs" /></button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input type="text" value={m.name} onChange={e => update(m.id, { name: e.target.value })} placeholder="Ad Soyad" className="border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white" />
            <input type="text" value={m.role} onChange={e => update(m.id, { role: e.target.value })} placeholder="Pozisyon" className="border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white" />
          </div>
          <textarea value={m.bio} onChange={e => update(m.id, { bio: e.target.value })} rows={2} placeholder="Biyografi" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none bg-white" />
          <ImageUploader value={m.image_url} onChange={url => update(m.id, { image_url: url })} />
        </div>
      ))}
      <button onClick={add} className="w-full py-2 border border-dashed border-stone-300 rounded-lg text-xs text-stone-500 hover:border-amber-400 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap">+ Üye Ekle</button>
    </div>
  );
}

function BlockEditorSwitch({ block, onChange }: { block: PageBlock; onChange: (b: PageBlock) => void }) {
  switch (block.type) {
    case 'heading': return <HeadingEditor block={block} onChange={onChange as (b: PageBlockHeading) => void} />;
    case 'text': return <TextEditor block={block} onChange={onChange as (b: PageBlockText) => void} />;
    case 'image': return <ImageEditor block={block} onChange={onChange as (b: PageBlockImage) => void} />;
    case 'button': return <ButtonEditor block={block} onChange={onChange as (b: PageBlockButton) => void} />;
    case 'divider': return <DividerEditor block={block} onChange={onChange as (b: PageBlockDivider) => void} />;
    case 'spacer': return <SpacerEditor block={block} onChange={onChange as (b: PageBlockSpacer) => void} />;
    case 'hero': return <HeroEditor block={block} onChange={onChange as (b: PageBlockHero) => void} />;
    case 'faq_list': return <FaqEditor block={block} onChange={onChange as (b: PageBlockFaqList) => void} />;
    case 'features_grid': return <FeaturesEditor block={block} onChange={onChange as (b: PageBlockFeaturesGrid) => void} />;
    case 'stats': return <StatsEditor block={block} onChange={onChange as (b: PageBlockStats) => void} />;
    case 'team': return <TeamEditor block={block} onChange={onChange as (b: PageBlockTeam) => void} />;
    default: return null;
  }
}

// ─── Column Width to Tailwind ─────────────────────────────────────────────────
function colWidthClass(w: ColumnWidth): string {
  const map: Record<ColumnWidth, string> = {
    '100': 'w-full', '50': 'w-1/2', '33': 'w-1/3', '67': 'w-2/3',
    '25': 'w-1/4', '75': 'w-3/4', '30': 'w-[30%]', '70': 'w-[70%]',
  };
  return map[w] ?? 'w-full';
}

// ─── Block Panel (right side) ─────────────────────────────────────────────────
interface BlockPanelProps {
  block: PageBlock;
  onUpdate: (b: PageBlock) => void;
  onDelete: () => void;
  onClose: () => void;
}

function BlockPanel({ block, onUpdate, onDelete, onClose }: BlockPanelProps) {
  const def = BLOCK_TYPES.find(t => t.type === block.type);
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-stone-200 flex-shrink-0">
        <div className="w-7 h-7 flex items-center justify-center bg-amber-50 rounded-lg">
          <i className={`${def?.icon} text-amber-600 text-sm`} />
        </div>
        <span className="text-sm font-semibold text-stone-800 flex-1">{def?.label}</span>
        <button onClick={onDelete} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors cursor-pointer" title="Bloğu Sil">
          <i className="ri-delete-bin-line text-sm" />
        </button>
        <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 transition-colors cursor-pointer">
          <i className="ri-close-line text-sm" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        <BlockEditorSwitch block={block} onChange={onUpdate} />
      </div>
    </div>
  );
}

// ─── Section Settings Panel ───────────────────────────────────────────────────
interface SectionPanelProps {
  section: PageSection;
  onUpdate: (s: PageSection) => void;
  onClose: () => void;
}

function SectionPanel({ section, onUpdate, onClose }: SectionPanelProps) {
  const PADDING_OPTIONS: { value: PageSection['padding_y']; label: string }[] = [
    { value: 'none', label: 'Yok' },
    { value: 'sm', label: 'Az (16px)' },
    { value: 'md', label: 'Orta (40px)' },
    { value: 'lg', label: 'Fazla (64px)' },
    { value: 'xl', label: 'Çok Fazla (96px)' },
  ];
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-stone-200 flex-shrink-0">
        <div className="w-7 h-7 flex items-center justify-center bg-stone-100 rounded-lg">
          <i className="ri-layout-row-line text-stone-600 text-sm" />
        </div>
        <span className="text-sm font-semibold text-stone-800 flex-1">Section Ayarları</span>
        <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 transition-colors cursor-pointer">
          <i className="ri-close-line text-sm" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-2">Arka Plan Rengi</label>
          <div className="flex items-center gap-2">
            <input type="color" value={section.background_color} onChange={e => onUpdate({ ...section, background_color: e.target.value })} className="w-9 h-9 rounded-lg border border-stone-200 cursor-pointer" />
            <input type="text" value={section.background_color} onChange={e => onUpdate({ ...section, background_color: e.target.value })} className="flex-1 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-600 mb-2">Dikey Boşluk</label>
          <div className="grid grid-cols-1 gap-1.5">
            {PADDING_OPTIONS.map(opt => (
              <button key={opt.value} onClick={() => onUpdate({ ...section, padding_y: opt.value })} className={`px-3 py-2 rounded-lg text-xs font-medium border text-left transition-all cursor-pointer whitespace-nowrap ${section.padding_y === opt.value ? 'bg-amber-600 text-white border-amber-600' : 'border-stone-200 text-stone-600 hover:border-stone-300'}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Add Block Picker ─────────────────────────────────────────────────────────
interface AddBlockPickerProps {
  onAdd: (type: PageBlockType) => void;
  onClose: () => void;
}

function AddBlockPicker({ onAdd, onClose }: AddBlockPickerProps) {
  const groups = ['Temel', 'Gelişmiş'];
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-stone-200 flex-shrink-0">
        <span className="text-sm font-semibold text-stone-800 flex-1">Blok Ekle</span>
        <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 transition-colors cursor-pointer">
          <i className="ri-close-line text-sm" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {groups.map(group => (
          <div key={group}>
            <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-2">{group}</p>
            <div className="grid grid-cols-2 gap-2">
              {BLOCK_TYPES.filter(bt => bt.group === group).map(bt => (
                <button key={bt.type} onClick={() => onAdd(bt.type)} className="flex items-center gap-2 p-3 rounded-xl border border-stone-200 hover:border-amber-400 hover:bg-amber-50 transition-all cursor-pointer text-left">
                  <div className="w-8 h-8 flex items-center justify-center bg-stone-100 rounded-lg flex-shrink-0">
                    <i className={`${bt.icon} text-stone-600 text-sm`} />
                  </div>
                  <span className="text-xs font-medium text-stone-700 leading-tight">{bt.label}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Add Section Picker ───────────────────────────────────────────────────────
interface AddSectionPickerProps {
  onAdd: (layout: SectionLayout) => void;
  onClose: () => void;
}

function AddSectionPicker({ onAdd, onClose }: AddSectionPickerProps) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-stone-200 flex-shrink-0">
        <span className="text-sm font-semibold text-stone-800 flex-1">Section Düzeni Seç</span>
        <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 transition-colors cursor-pointer">
          <i className="ri-close-line text-sm" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {SECTION_LAYOUTS.map(layout => (
          <button
            key={layout.id}
            onClick={() => onAdd(layout.id)}
            className="w-full flex items-center gap-4 p-4 rounded-xl border border-stone-200 hover:border-amber-400 hover:bg-amber-50 transition-all cursor-pointer text-left"
          >
            <div className="flex-1">
              <p className="text-sm font-semibold text-stone-800">{layout.label}</p>
              <p className="text-xs text-stone-400 mt-0.5">{layout.description}</p>
            </div>
            {/* Visual preview */}
            <div className="flex gap-1 flex-shrink-0">
              {layout.columns.map((w, i) => (
                <div
                  key={i}
                  className="h-8 bg-stone-200 rounded"
                  style={{ width: `${Number(w) * 0.6}px`, minWidth: '12px' }}
                />
              ))}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Main PageEditor ──────────────────────────────────────────────────────────

type PanelState =
  | { type: 'none' }
  | { type: 'add_section' }
  | { type: 'section_settings'; sectionId: string }
  | { type: 'add_block'; sectionId: string; columnId: string }
  | { type: 'edit_block'; sectionId: string; columnId: string; blockId: string };

export default function PageEditor({ page, onSave, onBack }: Props) {
  const [local, setLocal] = useState<CustomPage>(JSON.parse(JSON.stringify(page)));
  const [tab, setTab] = useState<EditorTab>('layout');
  const [panel, setPanel] = useState<PanelState>({ type: 'none' });
  const [saved, setSaved] = useState(false);

  // ── Section helpers ──
  const addSection = (layout: SectionLayout) => {
    const s = createSection(layout);
    setLocal(prev => ({ ...prev, sections: [...prev.sections, s] }));
    setPanel({ type: 'none' });
  };

  const removeSection = (sectionId: string) => {
    setLocal(prev => ({ ...prev, sections: prev.sections.filter(s => s.id !== sectionId) }));
    setPanel({ type: 'none' });
  };

  const moveSection = (sectionId: string, dir: 'up' | 'down') => {
    setLocal(prev => {
      const idx = prev.sections.findIndex(s => s.id === sectionId);
      if (idx < 0) return prev;
      const arr = [...prev.sections];
      const swap = dir === 'up' ? idx - 1 : idx + 1;
      if (swap < 0 || swap >= arr.length) return prev;
      [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
      return { ...prev, sections: arr };
    });
  };

  const updateSection = (updated: PageSection) => {
    setLocal(prev => ({ ...prev, sections: prev.sections.map(s => s.id === updated.id ? updated : s) }));
  };

  // ── Block helpers ──
  const addBlock = (sectionId: string, columnId: string, type: PageBlockType) => {
    const block = createBlock(type);
    setLocal(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.id === sectionId
          ? { ...s, columns: s.columns.map(c => c.id === columnId ? { ...c, blocks: [...c.blocks, block] } : c) }
          : s
      ),
    }));
    setPanel({ type: 'edit_block', sectionId, columnId, blockId: block.id });
  };

  const updateBlock = (sectionId: string, columnId: string, updated: PageBlock) => {
    setLocal(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.id === sectionId
          ? { ...s, columns: s.columns.map(c => c.id === columnId ? { ...c, blocks: c.blocks.map(b => b.id === updated.id ? updated : b) } : c) }
          : s
      ),
    }));
  };

  const removeBlock = (sectionId: string, columnId: string, blockId: string) => {
    setLocal(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.id === sectionId
          ? { ...s, columns: s.columns.map(c => c.id === columnId ? { ...c, blocks: c.blocks.filter(b => b.id !== blockId) } : c) }
          : s
      ),
    }));
    setPanel({ type: 'none' });
  };

  const moveBlock = (sectionId: string, columnId: string, blockId: string, dir: 'up' | 'down') => {
    setLocal(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.id === sectionId
          ? {
              ...s, columns: s.columns.map(c => {
                if (c.id !== columnId) return c;
                const idx = c.blocks.findIndex(b => b.id === blockId);
                if (idx < 0) return c;
                const arr = [...c.blocks];
                const swap = dir === 'up' ? idx - 1 : idx + 1;
                if (swap < 0 || swap >= arr.length) return c;
                [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
                return { ...c, blocks: arr };
              }),
            }
          : s
      ),
    }));
  };

  const handleSave = () => {
    onSave(local);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  // ── Get active block for panel ──
  const getActiveBlock = (): PageBlock | null => {
    if (panel.type !== 'edit_block') return null;
    const section = local.sections.find(s => s.id === panel.sectionId);
    const col = section?.columns.find(c => c.id === panel.columnId);
    return col?.blocks.find(b => b.id === panel.blockId) ?? null;
  };

  const getActiveSection = (): PageSection | null => {
    if (panel.type !== 'section_settings') return null;
    return local.sections.find(s => s.id === panel.sectionId) ?? null;
  };

  const blockLabel = (b: PageBlock) => BLOCK_TYPES.find(t => t.type === b.type)?.label ?? b.type;
  const blockIcon = (b: PageBlock) => BLOCK_TYPES.find(t => t.type === b.type)?.icon ?? 'ri-layout-line';

  const paddingClass: Record<PageSection['padding_y'], string> = {
    none: 'py-0', sm: 'py-4', md: 'py-10', lg: 'py-16', xl: 'py-24',
  };

  const hasPanel = panel.type !== 'none';

  return (
    <div className="flex flex-col h-full" style={{ minHeight: 0 }}>
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 flex-shrink-0">
        <button onClick={onBack} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 transition-colors cursor-pointer">
          <i className="ri-arrow-left-line text-stone-600" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-semibold text-stone-800 truncate">{local.name}</h2>
          <p className="text-xs text-stone-400">/{local.seo.slug}</p>
        </div>
        <button
          onClick={handleSave}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${saved ? 'bg-green-600 text-white' : 'bg-amber-600 text-white hover:bg-amber-700'}`}
        >
          <i className={saved ? 'ri-check-line' : 'ri-save-line'} />
          {saved ? 'Kaydedildi!' : 'Kaydet'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-stone-100 p-1 rounded-xl mb-4 flex-shrink-0">
        {([['layout', 'ri-layout-line', 'Düzen'], ['seo', 'ri-search-eye-line', 'SEO & URL']] as const).map(([id, icon, label]) => (
          <button key={id} onClick={() => setTab(id)} className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${tab === id ? 'bg-white text-stone-800' : 'text-stone-500 hover:text-stone-700'}`}>
            <i className={icon} />
            {label}
          </button>
        ))}
      </div>

      {/* Layout Tab */}
      {tab === 'layout' && (
        <div className="flex gap-3 flex-1 min-h-0">
          {/* Left: Section/Column/Block tree */}
          <div className="flex-1 overflow-y-auto space-y-3 min-w-0">
            {local.sections.length === 0 && (
              <div className="bg-white rounded-xl border border-dashed border-stone-300 p-8 text-center">
                <div className="w-12 h-12 flex items-center justify-center bg-stone-100 rounded-xl mx-auto mb-3">
                  <i className="ri-layout-row-line text-xl text-stone-400" />
                </div>
                <p className="text-sm text-stone-500 mb-1">Henüz section yok</p>
                <p className="text-xs text-stone-400">Aşağıdan section ekleyerek başlayın</p>
              </div>
            )}

            {local.sections.map((section, sIdx) => {
              const layoutDef = SECTION_LAYOUTS.find(l => l.id === section.layout);
              return (
                <div key={section.id} className="bg-white rounded-xl border border-stone-200 overflow-hidden">
                  {/* Section header */}
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-stone-50 border-b border-stone-200">
                    <div className="flex gap-0.5 flex-shrink-0">
                      {section.columns.map((c, i) => (
                        <div key={i} className="h-4 bg-stone-300 rounded-sm" style={{ width: `${Number(c.width) * 0.3}px`, minWidth: '8px' }} />
                      ))}
                    </div>
                    <span className="text-xs font-medium text-stone-600 flex-1 truncate">{layoutDef?.label} — {layoutDef?.description}</span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button onClick={() => moveSection(section.id, 'up')} disabled={sIdx === 0} className="w-6 h-6 flex items-center justify-center rounded hover:bg-stone-200 text-stone-400 disabled:opacity-30 cursor-pointer transition-colors">
                        <i className="ri-arrow-up-s-line text-xs" />
                      </button>
                      <button onClick={() => moveSection(section.id, 'down')} disabled={sIdx === local.sections.length - 1} className="w-6 h-6 flex items-center justify-center rounded hover:bg-stone-200 text-stone-400 disabled:opacity-30 cursor-pointer transition-colors">
                        <i className="ri-arrow-down-s-line text-xs" />
                      </button>
                      <button onClick={() => setPanel({ type: 'section_settings', sectionId: section.id })} className="w-6 h-6 flex items-center justify-center rounded hover:bg-stone-200 text-stone-400 hover:text-stone-600 cursor-pointer transition-colors" title="Section Ayarları">
                        <i className="ri-settings-3-line text-xs" />
                      </button>
                      <button onClick={() => removeSection(section.id)} className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-50 text-stone-400 hover:text-red-500 cursor-pointer transition-colors" title="Section Sil">
                        <i className="ri-delete-bin-line text-xs" />
                      </button>
                    </div>
                  </div>

                  {/* Columns */}
                  <div className={`flex gap-0 ${paddingClass[section.padding_y]}`} style={{ backgroundColor: section.background_color }}>
                    {section.columns.map((col, cIdx) => (
                      <div
                        key={col.id}
                        className={`${colWidthClass(col.width)} ${cIdx < section.columns.length - 1 ? 'border-r border-stone-100' : ''} px-3`}
                      >
                        {/* Column label */}
                        <div className="text-center mb-2">
                          <span className="text-xs text-stone-400 bg-stone-100 px-2 py-0.5 rounded-full">{col.width}%</span>
                        </div>

                        {/* Blocks in column */}
                        <div className="space-y-1.5">
                          {col.blocks.map((block, bIdx) => {
                            const isActive = panel.type === 'edit_block' && panel.blockId === block.id;
                            return (
                              <div
                                key={block.id}
                                className={`group flex items-center gap-2 px-2.5 py-2 rounded-lg border cursor-pointer transition-all ${isActive ? 'border-amber-400 bg-amber-50' : 'border-stone-200 bg-white hover:border-stone-300'}`}
                                onClick={() => setPanel({ type: 'edit_block', sectionId: section.id, columnId: col.id, blockId: block.id })}
                              >
                                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                                  <i className={`${blockIcon(block)} text-xs ${isActive ? 'text-amber-600' : 'text-stone-400'}`} />
                                </div>
                                <span className="text-xs font-medium text-stone-700 flex-1 truncate">{blockLabel(block)}</span>
                                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                                  <button onClick={e => { e.stopPropagation(); moveBlock(section.id, col.id, block.id, 'up'); }} disabled={bIdx === 0} className="w-5 h-5 flex items-center justify-center rounded hover:bg-stone-200 text-stone-400 disabled:opacity-30 cursor-pointer">
                                    <i className="ri-arrow-up-s-line text-xs" />
                                  </button>
                                  <button onClick={e => { e.stopPropagation(); moveBlock(section.id, col.id, block.id, 'down'); }} disabled={bIdx === col.blocks.length - 1} className="w-5 h-5 flex items-center justify-center rounded hover:bg-stone-200 text-stone-400 disabled:opacity-30 cursor-pointer">
                                    <i className="ri-arrow-down-s-line text-xs" />
                                  </button>
                                  <button onClick={e => { e.stopPropagation(); removeBlock(section.id, col.id, block.id); }} className="w-5 h-5 flex items-center justify-center rounded hover:bg-red-50 text-stone-400 hover:text-red-500 cursor-pointer">
                                    <i className="ri-close-line text-xs" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Add block button */}
                        <button
                          onClick={() => setPanel({ type: 'add_block', sectionId: section.id, columnId: col.id })}
                          className="w-full mt-2 py-1.5 border border-dashed border-stone-300 rounded-lg text-xs text-stone-400 hover:border-amber-400 hover:text-amber-600 transition-colors cursor-pointer flex items-center justify-center gap-1 whitespace-nowrap"
                        >
                          <i className="ri-add-line" />
                          Blok Ekle
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Add Section Button */}
            <button
              onClick={() => setPanel({ type: 'add_section' })}
              className="w-full py-3 border border-dashed border-stone-300 rounded-xl text-sm text-stone-500 hover:border-amber-400 hover:text-amber-600 transition-colors cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <i className="ri-add-line" />
              Section Ekle
            </button>
          </div>

          {/* Right: Panel */}
          {hasPanel && (
            <div className="w-64 flex-shrink-0 bg-white rounded-xl border border-stone-200 overflow-hidden flex flex-col" style={{ maxHeight: '600px' }}>
              {panel.type === 'add_section' && (
                <AddSectionPicker onAdd={addSection} onClose={() => setPanel({ type: 'none' })} />
              )}
              {panel.type === 'section_settings' && getActiveSection() && (
                <SectionPanel
                  section={getActiveSection()!}
                  onUpdate={updateSection}
                  onClose={() => setPanel({ type: 'none' })}
                />
              )}
              {panel.type === 'add_block' && (
                <AddBlockPicker
                  onAdd={type => addBlock(panel.sectionId, panel.columnId, type)}
                  onClose={() => setPanel({ type: 'none' })}
                />
              )}
              {panel.type === 'edit_block' && getActiveBlock() && (
                <BlockPanel
                  block={getActiveBlock()!}
                  onUpdate={b => updateBlock(panel.sectionId, panel.columnId, b)}
                  onDelete={() => removeBlock(panel.sectionId, panel.columnId, panel.blockId)}
                  onClose={() => setPanel({ type: 'none' })}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* SEO Tab */}
      {tab === 'seo' && (
        <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-4 overflow-y-auto">
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Sayfa Adı (Panel)</label>
            <input type="text" value={local.name} onChange={e => setLocal(prev => ({ ...prev, name: e.target.value }))} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">URL (Slug)</label>
            <div className="flex items-center">
              <span className="px-3 py-2 bg-stone-100 border border-r-0 border-stone-200 rounded-l-lg text-xs text-stone-500 whitespace-nowrap">/p/</span>
              <input
                type="text"
                value={local.seo.slug}
                onChange={e => setLocal(prev => ({ ...prev, seo: { ...prev.seo, slug: e.target.value.replace(/^\//, '').replace(/[^a-z0-9-]/g, '-') } }))}
                className="flex-1 border border-stone-200 rounded-r-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">SEO Başlığı <span className="text-stone-400 font-normal">(max 60 karakter)</span></label>
            <input type="text" value={local.seo.title} maxLength={60} onChange={e => setLocal(prev => ({ ...prev, seo: { ...prev.seo, title: e.target.value } }))} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            <p className="text-xs text-stone-400 mt-1">{local.seo.title.length}/60</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Meta Açıklama <span className="text-stone-400 font-normal">(120-160 karakter)</span></label>
            <textarea value={local.seo.description} maxLength={160} onChange={e => setLocal(prev => ({ ...prev, seo: { ...prev.seo, description: e.target.value } }))} rows={3} className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none" />
            <p className="text-xs text-stone-400 mt-1">{local.seo.description.length}/160</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Anahtar Kelimeler</label>
            <input type="text" value={local.seo.keywords} onChange={e => setLocal(prev => ({ ...prev, seo: { ...prev.seo, keywords: e.target.value } }))} placeholder="kahve, specialty, filtre kahve" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
          <div className="bg-stone-50 rounded-lg p-4 border border-stone-200">
            <p className="text-xs font-medium text-stone-500 mb-2">Google Önizleme</p>
            <p className="text-sm font-medium text-green-700 truncate">{window.location.origin}/p/{local.seo.slug}</p>
            <p className="text-sm font-semibold text-stone-800 mt-1 truncate">{local.seo.title || 'Sayfa Başlığı'}</p>
            <p className="text-xs text-stone-500 mt-0.5 line-clamp-2">{local.seo.description || 'Meta açıklama buraya gelecek...'}</p>
          </div>
        </div>
      )}
    </div>
  );
}
